import React, { memo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { css } from "@emotion/react";

import { PromotionCode } from "../../config/graphQLTypes";
import { useAppState } from "../contexts/AppContext";
import useSetState from "../hooks/useSetState";
import * as storage from "../includes/localStorage";
import { AUTH_URL, getUrl, PAYMENT_URL } from "../includes/pricingAuthRedirects";
import theme from "../../theme";
import BlacklistCountryRedirect from "./utils/BlacklistCountryRedirect";
import Button from "./Button";
import { DropdownOption } from "./form/DropDown";
import OrderSummary from "./OrderSummary";
import Payment from "./Payment";
import PlanSelectionFooter from "./PlanSelectionFooter";
import PlanSelectionItem from "./PlanSelectionItem";
import PlanSelectionPlanType from "./PlanSelectionPlanType";
import PlanSelectionUsers from "./PlanSelectionUsers";
import Loading from "./utils/Loading";
import StripeLoader from "./utils/StripeLoader";

type Props = {
    isPayment: boolean,
};

type DefaultState = {
    isSubmitting: boolean,
    promotionCode: PromotionCode | null,
};

const PlanSelection: React.FC<Props> = ({ isPayment }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [appState, setAppState] = useAppState();
    const urlPlanType = new URLSearchParams(location?.search).get('plan-type');

    useEffect(() => {
        if (!appState.currentNumberOfUsers.id && !appState.currentPlanType.id) {
            // Get persisted storage value
            const storageCurrentPlanTypeID = (urlPlanType === 'personal' || urlPlanType === 'family') ?
                urlPlanType :
                (storage.retrieveValue('currentPlanTypeID') ?? 'personal');
            // Find plan based on storage value, or set to default.
            const currentPlanType = PLAN_TYPES.find(
                (planType: DropdownOption) => planType.id === storageCurrentPlanTypeID
            ) || PLAN_TYPES[0];

            // Get persisted storage value
            const storageCurrentNumberOfUsersID = storage.retrieveValue('currentNumberOfUsersID');
            // Find number of users based on storage value, or set to default.
            let currentNumberOfUsers = NUMBER_OF_USERS.find((numberOfUsers: DropdownOption) => (
                numberOfUsers.id === storageCurrentNumberOfUsersID
            ));

            if (!currentNumberOfUsers) {
                if (currentPlanType.id === 'family') {
                    currentNumberOfUsers = NUMBER_OF_USERS[0];
                } else {
                    currentNumberOfUsers = SINGLE_USER;
                }
            }
            setAppState({ currentNumberOfUsers, currentPlanType })
        }
    }, [appState.currentNumberOfUsers, appState.currentPlanType, setAppState, urlPlanType]);

    // Create state with base values and use storage values where appropriate
    const [state, setState] = useSetState<DefaultState>({
        isSubmitting: false,
        promotionCode: null,
    });

    // Set planType based on dropdown selection.  If changing plan, reset number of users.
    // Make sure storage is being persisted.
    const setPlanType = (planType: DropdownOption) => {
        if (appState.currentPlanType.id !== planType.id) {
            const newNumberOfUsers = planType.id === 'personal' ? SINGLE_USER : NUMBER_OF_USERS[0];

            setAppState({
                currentPlanType: planType,
                currentNumberOfUsers: newNumberOfUsers,
            });
            storage.persistValue('currentPlanTypeID', planType.id);
            storage.persistValue('currentNumberOfUsersID', newNumberOfUsers.id);
        }
    };

    const setCurrentPlan = (plan: StripePlan) => {
        setAppState({ selectedPlan: plan })
        storage.persistValue('currentPlanID', plan.id);
    };

    const setCurrentNumberOfUsers = (numberOfUsers: DropdownOption) => {
        setAppState({ currentNumberOfUsers: numberOfUsers });
        storage.persistValue('currentNumberOfUsersID', numberOfUsers.id);
    };

    const onSubmit = () => {
        setState({ isSubmitting: true });
        setAppState({ hasConfirmedPlan: true });
        navigate(getUrl(appState.user ? PAYMENT_URL : AUTH_URL));
        setState({ isSubmitting: false });
    };

    const onApplyPromoCode = useCallback(
        (promotionCode: PromotionCode | null) => setState({ promotionCode }),
        [setState]
    );

    // isCountryLookupSettled: true once the country lookup has finished, whether it
    // found a match or not — lets the UI stop waiting either way.
    const isCurrencyReady = appState.hasLoadedCountryCodes && appState.isCountryLookupSettled;

    return (
        <StripeLoader>
            <div>
                <div css={containerStyle(isPayment)}>
                    {!isPayment && (
                        <div css={leftContainerStyle}>
                            <div css={planSelectionContainerStyle}>
                                <PlanSelectionPlanType
                                    options={PLAN_TYPES}
                                    selected={appState.currentPlanType}
                                    onChange={setPlanType}
                                />
                                <PlanSelectionUsers
                                    currentPlanType={appState.currentPlanType}
                                    options={NUMBER_OF_USERS}
                                    selected={appState.currentNumberOfUsers}
                                    onChange={setCurrentNumberOfUsers}
                                />
                            </div>
                            <div>
                                <div>
                                    {(appState.isLoadingPlans || !isCurrencyReady) && (
                                        <Loading />
                                    )}
                                    <div css={stripePlansContainerStyle}>
                                        {isCurrencyReady && appState.plans && appState.plans.map((plan: StripePlan, index: number) => (
                                            <PlanSelectionItem
                                                key={index}
                                                isSelected={plan.Interval === appState.selectedPlan?.Interval}
                                                numberOfUsers={appState.currentNumberOfUsers.value}
                                                onPlanSelect={setCurrentPlan}
                                                plan={plan}
                                                currencySymbol={appState.selectedCountry?.currency || "$"}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <PlanSelectionFooter
                                    plan={appState.selectedPlan}
                                    isSubmitting={state.isSubmitting}
                                    onClick={onSubmit}
                                />
                            </div>
                        </div>
                    )}
                    {isPayment && (
                        <div>
                            <BlacklistCountryRedirect />
                            <Payment
                                promotionCode={state.promotionCode}
                                numberOfUsers={appState.currentNumberOfUsers.value}
                            />
                        </div>
                    )}
                    {isCurrencyReady && (
                        <div css={orderSummaryContainerStyle(isPayment)}>
                            <OrderSummary
                                promotionCode={state.promotionCode}
                                onApplyPromoCode={onApplyPromoCode}
                                plan={appState.selectedPlan}
                                planType={appState.currentPlanType.value}
                                numberOfUsers={appState.currentNumberOfUsers.value}
                                currencySymbol={appState.selectedCountry?.currency || "$"}
                            />
                        </div>
                    )}
                </div>
                {!isPayment && (
                    <div css={buttonStyle}>
                        <Button
                            color={"secondary"}
                            disabled={!appState.selectedPlan}
                            disabledTooltipMessage={"Please select a plan"}
                            forceLoading={state.isSubmitting}
                            onClick={onSubmit}
                            size="lg"
                            type="submit"
                            rounded
                        >
                            Continue
                        </Button>
                    </div>
                )}
            </div>
        </StripeLoader>
    );
};

const PLAN_TYPES: DropdownOption[] = [
    {
        id: 'personal',
        value: 'Personal',
    },
    {
        id: 'family',
        value: 'Family',
    }
];

const SINGLE_USER = {
    id: '1',
    value: '1',
};

const NUMBER_OF_USERS: DropdownOption[] = [
    {
        id: '2',
        value: '2',
    },
    {
        id: '3',
        value: '3',
    },
    {
        id: '4',
        value: '4',
    },
    {
        id: '5',
        value: '5',
    },
    {
        id: '6',
        value: '6',
    },
];

export default memo(PlanSelection);

const planSelectionContainerStyle = css`
    display: flex;
    width: 100%;
    align-items: center;
    background: #efefef;
    border-radius: 16px;
    justify-content: space-between;
`;

const containerStyle = (isPayment?: boolean) => css`
    display: flex;
    justify-content: space-between;

    ${theme.breakpoints.down('md')} {
        flex-direction: ${isPayment ? 'column-reverse' : 'column'};
    }
`;

const leftContainerStyle = css`
    flex-grow: 1;
    padding-right: 30px;
`;

const stripePlansContainerStyle = css`
    display: flex;
    justify-content: space-between;
`;

const buttonStyle = css`
    width: 100%;
    display: none;
    margin-top: 22px;

    ${theme.breakpoints.down('md')} {
        display: flex;
        justify-content: center;
    }
`;

const orderSummaryContainerStyle = (isPayment?: boolean) => css`
    ${isPayment && css`
        margin-bottom: 40px;
    `}
`;
